import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { JsonLd } from "@/components/JsonLd";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import { DeferredExitIntent } from "@/components/DeferredExitIntent";
import { RootErrorBoundary, RootNotFound } from "@/components/RootBoundaries";
import { SITE } from "@/content/site";
import { ADSENSE_LOADER_SCRIPT, WEB_VITALS_SCRIPT } from "@/lib/inline-scripts";
import { rootGraphSchema } from "@/lib/root-schemas";

import appCss from "../styles.css?url";
// Preloaded so the two fonts used above the fold start downloading with the
// stylesheet instead of after CSS parse (removes ~200-400ms of FOUT on 4G).
import interLatin from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import frauncesLatin from "@fontsource-variable/fraunces/files/fraunces-latin-wght-normal.woff2?url";
// The header wordmark is the LCP element on most routes — preload it so it
// starts downloading alongside the CSS instead of after HTML parse.
import headerLogo from "@/assets/logo-sm.webp";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0A0A0B" },
      { name: "author", content: SITE.fullName },
      { title: SITE.title },
      { name: "description", content: SITE.description },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:title", content: SITE.title },
      { property: "og:description", content: SITE.description },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@freelancertamal" },
      { name: "twitter:creator", content: "@freelancertamal" },
      { name: "twitter:title", content: SITE.title },
      { name: "twitter:description", content: SITE.description },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    scripts: [
      { children: ADSENSE_LOADER_SCRIPT },
      { children: WEB_VITALS_SCRIPT },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preload", as: "font", type: "font/woff2", href: interLatin, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: frauncesLatin, crossOrigin: "anonymous" },
      { rel: "preload", as: "image", href: headerLogo, fetchPriority: "high" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "alternate", type: "application/rss+xml", title: `${SITE.name} — Blog RSS`, href: `${SITE.url}/rss.xml` },
      { rel: "alternate", type: "application/feed+json", title: `${SITE.name} — Blog JSON Feed`, href: `${SITE.url}/feed.json` },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: RootNotFound,
  errorComponent: RootErrorBoundary,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <JsonLd data={rootGraphSchema} />
      <Header />
      <main className="pt-24">
        <Outlet />
      </main>
      <Footer />
      <StickyMobileCTA />
      <DeferredExitIntent />
    </QueryClientProvider>
  );
}
